import React, { useEffect, useRef, useState } from 'react';
import { DataGrid, GridOverlay, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import { getAllNotificationList } from '../services/notification.service';
import * as CryptoJS from 'crypto-js';
import Box from '@mui/material/Box';
import { FormControl, InputLabel, MenuItem, Pagination, Select } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import './Customnotificationlist.css';

function getFormattedDate(dateString) {
    if (dateString != null) {
        let dte   = new Date(dateString);
        let year  = dte.getFullYear();
        let month = dte.getMonth() + 1;
        let day   = dte.getDate();
        if (month < 10) { month = '0' + month; }
        if (day   < 10) { day   = '0' + day;   }
        return year + '-' + month + '-' + day;
    } else {
        return null;
    }
}

const columns = [
    { field: 'module',        editable: false, hide: false, headerName: 'Module',      width: 220 },
    { field: 'message',       editable: false, hide: false, headerName: 'Message',     width: 500 },
    { field: 'createdByName', editable: false, hide: false, headerName: 'Username',    width: 250 },
    {
        field: 'updated_at', editable: false, hide: false, headerName: 'Date & Time', width: 250,
        renderCell: (params) => <p>{getFormattedDate(params.row.updated_at)}</p>,
    },
];

function CustomToolbar() {
    return (
        <GridToolbarContainer>
            <GridToolbarExport />
        </GridToolbarContainer>
    );
}

const CustomLoader = () => (
    <GridOverlay>
        <CircularProgress color="primary" size={30} />
    </GridOverlay>
);

function CustomNotificationList() {
    const [items,      setItems]      = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [page,       setPage]       = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [pageSize,   setPageSize]   = useState(50);

    // useRef — now properly imported above
    const abortRef = useRef(null);

    const calculateOffset = (p, ps) => (p - 1) * ps;

    useEffect(() => {
        setLoading(true);

        const offset      = calculateOffset(page, pageSize);
        const EncryptData = CryptoJS.AES.encrypt(
            JSON.stringify({
                param_data: { offset, pageSize },
                authToken:  sessionStorage.getItem('LoggedInToken'),
            }),
            process.env.REACT_APP_ENCRYPT_SECRET_KEY
        ).toString();

        getAllNotificationList({ token: EncryptData }).then((res) => {
            if (res?.data?.data?.length) {
                setItems(res.data.data);
                setTotalItems(res.data.total || 0);
            } else {
                setItems([]);
                setTotalItems(0);
            }
            setLoading(false);
        }).catch(() => setLoading(false));

    }, [page, pageSize]);

    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
        setPage(1);
    };

    const handlePageChange = (e, value) => {
        setPage(value);
    };

    const totalPages = Math.ceil(totalItems / pageSize);

    return (
        <div id="cnl-page" className="cnl-page">

            {/* ── Page Header ── */}
            <div id="cnl-header" className="cnl-header">
                <div className="cnl-header-left">
                    <div className="cnl-header-icon">
                        <i className="bi bi-bell-fill"></i>
                    </div>
                    <div>
                        <h1 className="cnl-title">Notification List</h1>
                        <p className="cnl-subtitle">View all system notifications</p>
                    </div>
                </div>
                {totalItems > 0 && (
                    <div className="al-record-badge" id="cnl-record-badge">
                        <i className="bi bi-collection"></i>
                        {totalItems} Total Records
                    </div>
                )}
            </div>

            {/* ── Data Grid Card ── */}
            <div className="al-grid-card" id="cnl-grid-card">
                <div className="al-grid-card-header" id="cnl-grid-header">
                    <span className="al-grid-card-title">
                        <i className="bi bi-table"></i>
                        Notification Records
                    </span>
                    {items.length > 0 && (
                        <span className="al-showing-badge">
                            Showing <strong>{items.length}</strong> of <strong>{totalItems}</strong>
                        </span>
                    )}
                </div>

                <Box id="cnl-grid-box">
                    <DataGrid
                        rows={items}
                        getRowId={(row) => row.id}
                        columns={columns}
                        autoHeight
                        disableSelectionOnClick
                        sx={{
                            border: 'none',
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: '0.82rem',
                            '& .MuiDataGrid-columnHeaders': {
                                background: '#f8f9fc',
                                borderBottom: '1px solid #e2e6ef',
                            },
                            '& .MuiDataGrid-columnHeaderTitle': {
                                fontWeight: 700,
                                fontSize: '0.78rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                color: '#1a3a5c',
                            },
                            '& .MuiDataGrid-row:hover': {
                                background: '#eff8ff',
                            },
                            '& .MuiDataGrid-cell': {
                                borderBottom: '1px solid #f1f5f9',
                                color: '#1e293b',
                            },
                        }}
                        components={{
                            LoadingOverlay: CustomLoader,
                            Toolbar:        CustomToolbar,
                            Pagination: () => (
                                <div className="al-pagination-bar" id="cnl-pagination">
                                    <span className="al-showing-badge">
                                        Showing <strong>{items.length}</strong> of <strong>{totalItems}</strong>
                                    </span>
                                    <div className="al-page-size-row">
                                        <span className="al-page-size-label">Rows per page</span>
                                        <FormControl variant="outlined" size="small" sx={{ minWidth: 80 }}>
                                            <Select
                                                value={pageSize}
                                                onChange={handlePageSizeChange}
                                                sx={{ fontSize: '0.78rem', fontFamily: "'DM Sans', sans-serif" }}
                                            >
                                                {[50, 100, 200, 500].map((size) => (
                                                    <MenuItem key={size} value={size} sx={{ fontSize: '0.78rem' }}>
                                                        {size}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </div>
                                    <Pagination
                                        count={totalPages}
                                        page={page}
                                        onChange={handlePageChange}
                                        variant="outlined"
                                        shape="rounded"
                                        sx={{
                                            '& .Mui-selected': {
                                                backgroundColor: '#0e8aca !important',
                                                borderColor:     '#0e8aca !important',
                                                color:           '#fff !important',
                                            },
                                            '& .MuiButtonBase-root': {
                                                fontFamily: "'DM Sans', sans-serif",
                                                fontSize:   '0.76rem',
                                            },
                                        }}
                                    />
                                </div>
                            ),
                        }}
                        loading={loading}
                    />
                </Box>
            </div>
        </div>
    );
}

export default CustomNotificationList;